import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import BackButton from 'components/backButton'
import { Button, Spinner } from 'components/common'
import { FreshCategoryForm } from 'components/freshCategory'
import {
  fetchFreshCategory,
  updateFreshCategory,
} from 'utils/api/freshCategoryApi'
import { PAGE_FRESH_CATEGORY, PAGE_NOT_FOUND } from 'utils/constants'
import { handleError } from 'utils/helpers/handleError'
import {
  FreshCategoryFormFields,
  freshCategorySchema,
} from 'utils/schema/freshCategorySchema'

const UpdateFreshCategory = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState<boolean>(true)
  const [category, setCategory] = useState<any>({})
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FreshCategoryFormFields>({
    resolver: yupResolver(freshCategorySchema),
  })

  const getCategoryDetails = async (categoryId: string) => {
    try {
      const { data } = await fetchFreshCategory(categoryId)
      setCategory(data)
      reset({
        name: data?.name ?? '',
        // Stored as an array, edited as a comma-separated list
        excludedBarcodes: (data?.excludedBarcodes ?? []).join(', '),
        image: data?.image ?? '',
        isActive: data?.isActive ?? true,
      })
    } catch {
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) getCategoryDetails(id)
  }, [])

  if (loading) return <Spinner />

  const handleUpdate = async (data: FreshCategoryFormFields) => {
    if (!id) return

    const toastId = toast.loading('Updating category...', {
      duration: Infinity,
    })
    try {
      await updateFreshCategory(id, data)
      toast.success('Category updated successfully!', {
        id: toastId,
        duration: 3000,
      })
      navigate(PAGE_FRESH_CATEGORY.path)
    } catch (error) {
      handleError(
        error,
        toastId,
        { default: 'Failed to update category!' },
        5000,
      )
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Update Fresh Category</title>
      </Helmet>

      <BackButton />

      <form onSubmit={handleSubmit(handleUpdate)}>
        <FreshCategoryForm
          register={register}
          control={control}
          setValue={setValue}
          errors={errors}
          category={category}
          onUploadingChange={setIsUploading}
        />

        <Button
          buttonText="Update Category"
          className="mt-7 w-44"
          disabled={isUploading}
        />
      </form>
    </>
  )
}

export default UpdateFreshCategory
